import { lazyAssets, LazyAsset } from '../lazy';
import { ActionInfo } from '../models/ActionInfo';
import { dataContext } from '../FlowSync/context';
import { dataManager } from '../APIEntity';
import { isHubOnly } from '../utils/hub-runtime';
import type { GitOrigin } from '../models';

/**
 * Provider abstraction for git-hosting platforms. v1 implements only `github`;
 * other values shape the API but return a clean ApiFailResponse from the backend.
 * GitLab nested namespaces will need a multi-level `owner` extension when added.
 */
export type GitProvider = 'github' | 'gitlab' | 'bitbucket';

export interface RepoSummary {
  provider: GitProvider;
  owner: string;
  name: string;
  git_origin: GitOrigin;
  full_name: string;
  private: boolean;
  default_branch: string;
  pushed_at: string; // ISO timestamp
  role: 'admin' | 'write' | 'read';
  html_url: string;
  description: string;
  fork: boolean;
}

export interface BranchSummary {
  name: string;
  protected: boolean;
  /** ISO timestamp of the branch's last commit; '' when the backend fell back
   *  to REST, which reports no date at all. */
  updated_at: string;
}

export interface RepoInvitation {
  id: number;
  repo: { owner: string; name: string; full_name: string; private: boolean };
  inviter_login: string;
  permissions: string;
  invited_at: string;
  html_url: string;
}

interface ListReposPage {
  repos: RepoSummary[];
  next_page: number | null;
  page: number;
}

function _userInfo() {
  // The repo/* action is user-scoped; the user typeid comes from dataContext
  // (populated at bootstrap). Throws clearly if missing rather than producing
  // a malformed URL (`/api/v1/graph/user/<empty>/repo/...` 404s silently).
  const userTypeId = dataContext.userTypeId;
  if (!userTypeId?.id) throw new Error('No user in context — bootstrap not ready');
  return userTypeId;
}

async function _fetchReposPage(provider: GitProvider, page: number): Promise<ListReposPage> {
  const user = _userInfo();
  const info = new ActionInfo('repo', user.type, user.id, 'POST');
  info.subpath = 'list';
  info.bodyParameters = { provider, page };
  const res = await dataManager.callAction<unknown, ListReposPage>(info);
  if (!res || !Array.isArray(res.repos)) {
    throw new Error('Invalid /repo/list response');
  }
  return res;
}

/**
 * Returns the full list of repositories the user has access to via the given
 * provider. Walks pages sequentially using the server-supplied next_page
 * cursor; with per_page=100 a 500-repo account takes 5 sequential round-trips.
 *
 * Guarantees:
 *  - Starts the walk from page 1, then advances using `first.next_page` (so the
 *    second request hits the page the server pointed at, not a hardcoded `2`).
 *  - Monotonicity: each next cursor must be strictly greater than the current
 *    page; otherwise the walk stops. This prevents a misbehaving server from
 *    pinning us in an infinite loop.
 *  - Partial success: if a mid-walk fetch throws (transient 5xx, network blip)
 *    the accumulated pages so far are still returned, so the picker shows what
 *    we know rather than an empty list. The first-page error still propagates
 *    so the caller can render an explicit error state.
 *  - Safety cap at 50 pages (≈5000 repos).
 */
export function getRepos(provider: GitProvider): Promise<RepoSummary[]> {
  return lazyAssets.load(LazyAsset.GitRepos, { provider });
}

export async function fetchRepos(provider: GitProvider): Promise<RepoSummary[]> {
  const first = await _fetchReposPage(provider, 1);
  const all: RepoSummary[] = [...first.repos];
  let cursor = first.next_page;
  let lastPage = 1;
  const HARD_CAP = 50;
  while (cursor != null && cursor > lastPage && lastPage < HARD_CAP) {
    let next: ListReposPage;
    try {
      next = await _fetchReposPage(provider, cursor);
    } catch (err) {
      // Surface partial results rather than dropping everything we accumulated.
      console.warn('[git-providers] mid-walk fetch failed; returning partial repos', err);
      return all;
    }
    all.push(...next.repos);
    lastPage = cursor;
    cursor = next.next_page;
  }
  return all;
}

/** Who the connected account is, and the organizations the provider reports for it. An org that
 *  restricts third-party apps is not among them even for a member — the provider hides it. */
export interface GitOrgs {
  login: string;
  orgs: { login: string; avatar_url: string }[];
}

/** One organization's repos the connected account can see. `restricted`: the org exists but shows
 *  nothing — what an org that restricts third-party apps looks like (or an empty one). */
export interface GitOrgRepos {
  owner: string;
  repos: RepoSummary[];
  restricted: boolean;
}

export async function fetchOrgs(provider: GitProvider): Promise<GitOrgs> {
  const user = _userInfo();
  const info = new ActionInfo('repo', user.type, user.id, 'POST');
  info.subpath = 'orgs';
  info.bodyParameters = { provider };
  const res = await dataManager.callAction<unknown, GitOrgs>(info);
  if (!res || !Array.isArray(res.orgs)) throw new Error('Invalid /repo/orgs response');
  return res;
}

/** Every page of one org's repos (same cursor walk and cap as `fetchRepos`). */
export async function fetchOrgRepos(provider: GitProvider, owner: string): Promise<GitOrgRepos> {
  const user = _userInfo();
  const page = async (n: number) => {
    const info = new ActionInfo('repo', user.type, user.id, 'POST');
    info.subpath = 'org-repos';
    info.bodyParameters = { provider, owner, page: n };
    const res = await dataManager.callAction<unknown, ListReposPage & { restricted: boolean }>(info);
    if (!res || !Array.isArray(res.repos)) throw new Error('Invalid /repo/org-repos response');
    return res;
  };
  const first = await page(1);
  const repos = [...first.repos];
  let cursor = first.next_page;
  let last = 1;
  while (cursor != null && cursor > last && last < 50) {
    const next = await page(cursor);
    repos.push(...next.repos);
    last = cursor;
    cursor = next.next_page;
  }
  return { owner, repos, restricted: first.restricted };
}

/** Create an initialized private repository owned by the connected GitHub user. */
export async function createPrivateRepo(provider: GitProvider, name: string): Promise<RepoSummary> {
  const user = _userInfo();
  const info = new ActionInfo('repo', user.type, user.id, 'POST');
  info.subpath = 'create';
  info.bodyParameters = { provider, name };
  const res = await dataManager.callAction<unknown, { repo?: RepoSummary }>(info);
  if (!res?.repo) throw new Error('Invalid /repo/create response');
  return res.repo;
}

function _isBranchSummary(x: unknown): x is BranchSummary {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.name === 'string';
}

export function getBranches(repo: { git_origin: GitOrigin }): Promise<BranchSummary[]> {
  return lazyAssets.load(LazyAsset.GitBranches, repo);
}

export async function fetchBranches(repo: { git_origin: GitOrigin }): Promise<BranchSummary[]> {
  const user = _userInfo();
  const info = new ActionInfo('repo', user.type, user.id, 'POST');
  info.subpath = 'branches';
  info.bodyParameters = { git_origin: repo.git_origin };
  // Backend returns ApiSuccessResponse(data=[{name, protected}, …]); the
  // dataManager unwraps `.data` once so we get the bare array here. Validate
  // the shape so a future schema drift doesn't silently feed garbage into the
  // picker.
  const res = await dataManager.callAction<unknown, unknown>(info);
  if (Array.isArray(res)) {
    return res.filter(_isBranchSummary).map((b) => ({
      name: b.name,
      protected: Boolean(b.protected),
      updated_at: typeof b.updated_at === 'string' ? b.updated_at : '',
    }));
  }
  console.warn('[git-providers] unexpected /repo/branches response shape', res);
  return [];
}

export function getInvitations(provider: GitProvider): Promise<RepoInvitation[]> {
  return lazyAssets.load(LazyAsset.GitInvitations, { provider });
}

export async function fetchInvitations(provider: GitProvider): Promise<RepoInvitation[]> {
  // The hub's `repo` action serves branches/list/create and nothing else, so
  // asking it for invitations earns a 500 on every clone-dialog open. Gated
  // here rather than at the call site so `respondInvitation` and any future
  // caller inherit the same answer: on a hub there are no invitations to show.
  if (isHubOnly()) return [];
  const user = _userInfo();
  const info = new ActionInfo('repo', user.type, user.id, 'POST');
  info.subpath = 'invitations';
  info.bodyParameters = { provider };
  const res = await dataManager.callAction<unknown, { invitations: RepoInvitation[] }>(info);
  return res?.invitations ?? [];
}

export async function respondInvitation(
  provider: GitProvider,
  invitationId: number,
  action: 'accept' | 'decline',
): Promise<void> {
  if (isHubOnly()) return;
  const user = _userInfo();
  const subpath = action === 'accept' ? 'invitation-accept' : 'invitation-decline';
  const info = new ActionInfo('repo', user.type, user.id, 'POST');
  info.subpath = subpath;
  info.bodyParameters = { provider, invitation_id: invitationId };
  await dataManager.callAction<unknown, { ok: boolean }>(info);
}
